import * as R from 'ramda';

export type OnboardStep = {
  id: string;
  title: (params: any) => string;
  description: string;
  tooltip?: string;
  button?: string;
  article?: string;
  link?: string;
};

export type OnboardStepKeys = 'review_documents' | 'perform_workout' | 'got_questions';
export type OnboardMap = { [KEY in OnboardStepKeys]: OnboardStep };

export const onboardSteps: OnboardMap = {
  review_documents: {
    id: 'review_documents',
    title: () => 'Hybrid Training Systems Course Prep',
    description: 'Before the start of the course, review sections 1 & 2 in the HTS Manual (do NOT complete the workbook assignments).',
    tooltip: 'Review the Manual',
    button: 'View HTS Manual',
  },
  perform_workout: {
    id: 'perform_workout',
    title: () => 'Perform a Pre-designed Workout in the App',
    tooltip: 'Perform a Workout',
    description: 'Time for you to learn how you and your clients will perform workouts and enter results. First, download the mobile app on your phone by searching for "gymcloud" in the <b>App Store</b> or <b>Google Play</b>. Download <b>Training App (by GymCloud)</b> and log in using your account information. <br /> <br />Then, you may start a workout using the tutorial button on the <b>Dashboard</b>, or by selecting a workout after navigating to <b>My Training Log</b> from the main menu.',
  },
  got_questions: {
    id: 'got_questions',
    title: () => 'Visit the Help Center',
    description: 'Visit the Help Center and review the Client articles to understand how your clients will experience the app.',
    tooltip: 'Visit the Help Center',
    button: 'End Tutorial',
    article: 'GymCloud Help Center',
    link: 'https://gymcloud.freshdesk.com/support/solutions/3000001953',
  },
};

const ideafitOnboardSteps = {
  add_client: {
    id: 'add_client',
    title: (params: any) => `Welcome to the Program Builder, ${params.first_name || ''}`,
    description: "This tutorial will take you through the Program Builder basics. You're welcome to view the guides for Fitness Professionals or jump right in if you think you're ready. After completing a step, return to the <b>Dashboard</b> for the next one. Linked articles will provide you with detailed info on these subjects. Let's get started by adding a client.",
    button: 'Add a Client',
    article: 'Article: How to Add & Manage Clients/Groups',
    link: 'https://gymcloud.freshdesk.com/support/solutions/articles/3000054925-how-to-add-and-manage-clients-groups',
  },
  assign_workout: {
    id: 'assign_workout',
    title: () => 'Assign a Workout',
    description: 'Now that you have added a Client, let’s assign them a workout. You can also complete this step by assigning a workout to yourself or to our demo client Josh Smith.',
    button: 'Assign a Workout',
    article: 'Article: How to Assign a Workout',
    link: 'https://gymcloud.freshdesk.com/support/solutions/articles/3000050220-how-to-assign-and-unassign-workouts-and-programs',
  },
  add_exercise: {
    id: 'add_exercise',
    title: () => 'Create an Exercise',
    description: 'We’ve preloaded your library with exercises, but what if you want to add your own? Click the button below to create your first exercise.',
    button: 'Create an Exercise',
    article: 'Article: How to Create Exercises',
    link: 'https://gymcloud.freshdesk.com/support/solutions/articles/3000054392-how-to-create-and-manage-exercises',
  },
  build_your_own_workout: {
    id: 'build_your_own_workout',
    title: () => 'Build Your Own Workout',
    description: 'The pre-loaded workouts are convenient, but adding your own is no sweat. Click below to learn how to build a workout.',
    button: 'Build a Workout',
    article: 'Article: How to Build a Workout',
    link: 'https://gymcloud.freshdesk.com/support/solutions/articles/3000053516-how-to-build-a-workout',
  },
  got_questions: {
    id: 'got_questions',
    title: () => 'Got Questions?',
    description: 'You’re probably well on your way to becoming an online training pro. But if you need support then check out our Help Center. Happy training!',
    button: 'End Tutorial',
    article: 'Help Center',
    link: 'https://gymcloud.freshdesk.com/support/solutions/3000001952',
  },
};

export const calculateOnboardPercentage = (namespace: string, userSteps: string[]) => {
  const steps = Object.keys(namespace === 'ideafit' ? ideafitOnboardSteps : onboardSteps);
  const completed = R.intersection(steps, userSteps);
  const percentage = 100 * (completed.length / steps.length);
  return Math.round(percentage);
};

export const getOnboardSteps = (namespace: string, userSteps: string[]) => {
  const steps = namespace === 'ideafit' ? ideafitOnboardSteps : onboardSteps;
  const keys = Object.keys(steps);
  const completed = R.intersection(keys, userSteps);
  const diff = R.difference(keys, completed);
  let current = diff[0];
  if (diff.length === 0) {
    current = R.last(keys)!;
  }

  const stepKeys = keys.map((name) => ({
    name,
    isCompleted: completed.indexOf(name) !== -1,
  }));

  return {
    steps,
    current,
    stepKeys,
  };
};
